import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Alert,
  CircularProgress
} from '@mui/material';
import { DateTime } from 'luxon';
import { employerApi } from '../services/apiClient';
import { toast } from 'react-toastify';

interface ResumeApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  application: any | null;
  onSuccess: () => void;
}

const ResumeApplicationDialog = ({ open, onClose, application, onSuccess }: ResumeApplicationDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pauseEndDate = application?.pauseConfiguration?.pauseEndDate
    ? DateTime.fromISO(application.pauseConfiguration.pauseEndDate)
    : null;

  const handleSubmit = async () => {
    if (!application) return;

    setIsSubmitting(true);
    try {
      await employerApi.post(`/applications/${application.id}/resume`);
      onSuccess();
    } catch (error: any) {
      console.error('Error resuming application:', error);
      toast.error(error?.message || 'Failed to resume application');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!application) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Resume Application</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            This will resume the application immediately. The employee will be charged from the next billing cycle.
          </Alert>

          {pauseEndDate && (
            <Typography variant="body2" color="text.secondary">
              The application was scheduled to automatically resume on{' '}
              <strong>{pauseEndDate.toFormat('d MMMM yyyy')}</strong>.
              Resuming now will activate it earlier than scheduled.
            </Typography>
          )}

          <Typography variant="body2">
            Are you sure you want to resume this application now?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ bgcolor: '#86eb85', '&:hover': { bgcolor: '#72d771' } }}
        >
          {isSubmitting ? 'Resuming...' : 'Confirm Resume'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResumeApplicationDialog;

